import React, { useState, useRef } from 'react';
import { Copy01Icon, Tick01Icon, TickDouble01Icon, DownloadCircle02Icon, LinkSquare01Icon } from 'hugeicons-react';
import { ChevronDown } from 'lucide-react';
import { copyRichText } from '../../../helpers/chatHelper';
import { Resource } from '../../../types/resource';
import { useHandleDownloadResource } from '../../../helpers/artifactHelper';
import { useResourceContext } from '../../../contexts/ResourceContext';
import { useChatContext, useSpaceContext } from '../../../contexts/AppDataContext';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRow,
} from '../../ui/dropdown-menu';
import '../../../Styles/Layout/Resources/Buttons/ResourcesActionButton.css';

interface ResourcesActionButtonProps {
  resource: Resource;
  onOpenFile?: () => void;
}

export const ResourcesActionButton: React.FC<ResourcesActionButtonProps> = ({ resource, onOpenFile }) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isRichCopied, setIsRichCopied] = useState(false);
  const [isHolding, setIsHolding] = useState(false);
  const holdTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleDownloadResource = useHandleDownloadResource();
  const { handleAddResourceToSpace, isReadOnlyResources } = useResourceContext();
  const { isSpaceChat } = useChatContext();
  const { spaceAttachments } = useSpaceContext();

  const isCode = resource.type === 'code';
  const isEssay = resource.type === 'essay';
  const showCopy = isCode || isEssay;

  const canDownload = isCode || isEssay;

  const isResourceInSpace = () => {
    if (!spaceAttachments?.length) return false;
    return spaceAttachments.some(
      (a) => !a.isTextAttachment && a.title === resource.title
    );
  };

  const canAddToSpace =
    !isReadOnlyResources &&
    isSpaceChat &&
    !isResourceInSpace() &&
    resource.type !== 'pdf' &&
    resource.type !== 'image' &&
    resource.type !== 'doc';

  const resetCopyState = () => {
    setTimeout(() => {
      setIsCopied(false);
      setIsRichCopied(false);
    }, 2000);
  };

  const handleMouseDown = () => {
    holdTimeout.current = setTimeout(() => {
      setIsHolding(true);
      copyRichText(resource.content);
      setIsRichCopied(true);
      resetCopyState();
    }, 500);
  };

  const handleMouseUp = () => {
    if (holdTimeout.current) clearTimeout(holdTimeout.current);
    if (!isHolding) {
      navigator.clipboard.writeText(resource.content);
      setIsCopied(true);
      resetCopyState();
    }
    setIsHolding(false);
  };

  const handleMouseLeave = () => {
    if (holdTimeout.current) clearTimeout(holdTimeout.current);
    setIsHolding(false);
  };

  if (!showCopy) return null;

  return (
    <div className="resources-action-button">
      <button
        type="button"
        className="rab-copy"
        aria-label="Copy"
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        {isRichCopied ? (
          <TickDouble01Icon size={15} />
        ) : isCopied ? (
          <Tick01Icon size={15} />
        ) : (
          <Copy01Icon size={15} />
        )}
        <span className="rab-copy-label">
          {isRichCopied ? 'Rich Copied' : isCopied ? 'Copied' : 'Copy'}
        </span>
      </button>

      <div className="rab-divider" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="rab-chevron" aria-label="More actions">
            <ChevronDown size={13} aria-hidden />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="end" className="rab-dropdown">
          {canDownload && (
            <DropdownMenuRow
              className="rab-dropdown-item"
              icon={<DownloadCircle02Icon />}
              text="Download"
              action={() => handleDownloadResource(resource)}
            />
          )}
          {canAddToSpace && (
            <DropdownMenuRow
              className="rab-dropdown-item"
              text="Add to Space"
              action={() => handleAddResourceToSpace(resource)}
            />
          )}
          {onOpenFile && (
            <DropdownMenuRow
              className="rab-dropdown-item"
              icon={<LinkSquare01Icon />}
              text="Show original file"
              action={onOpenFile}
            />
          )}
          {!canDownload && !canAddToSpace && !onOpenFile && (
            <DropdownMenuRow disabled className="rab-dropdown-item" text="No actions available" />
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
